// ============================================================================
// WORKQUEUE LIST VIEW — Central Intake Workqueue (Stage 2)
//
// Roles with access: tenant_admin, ops_manager, intake_coordinator,
//                    triage_nurse, scheduler, specialist
// gp_employee: NO access (RouteGuard blocks before this component renders).
//
// Architecture:
//   • listWorkqueueItems() fetches referrals + parallel workflow/state docs.
//   • Client-side filtering for status and outcome — no extra Firestore queries.
//   • Clicking a card opens WorkItemDrawer (slide-over).
//   • After any workflow write the drawer calls onWorkflowUpdated → list
//     patches the corresponding item in place without full reload.
//
// Zero-PHI invariant: no patient reference, age, or sex rendered.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  List, Loader2, AlertCircle, RefreshCw, Filter,
  ChevronRight, Clock, User, Flag, ClipboardList, CheckCircle2,
} from 'lucide-react';
import {
  WORKFLOW_STATUS, WORKFLOW_PRIORITY, RECOVERY_STAGE,
  listWorkqueueItems,
  listOpsActiveItems,
  isIntakeCoordinator,
} from '../core/index.js';
import { getReferral } from '../core/store.referrals.js';
import { findReferralInBuckets, classifyDeepLinkFailure } from '../core/deepLinkReferral.js';
import { useAuth } from '../auth/AuthContext.jsx';
import WorkItemDrawer from './WorkItemDrawer.jsx';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';

// ── Display config ────────────────────────────────────────────────────────────
// Status/outcome/priority badge visuals are centralized in src/core/statusConfig.js
// and rendered via <RGStatusBadge>. No local status color maps live here.

// Token-backed chip for derived, non-enum flags (recovery / override / stuck).
const CHIP_CLS = 'inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium';

const ACTIVE_FILTER_OPTIONS = [
  { value: 'ALL',                             label: 'All' },
  { value: WORKFLOW_STATUS.NEW,               label: 'New' },
  { value: WORKFLOW_STATUS.NEEDS_INFO,        label: 'Needs Info' },
  { value: WORKFLOW_STATUS.READY_TO_SCHEDULE, label: 'Ready to Schedule' },
  { value: WORKFLOW_STATUS.SCHEDULED,         label: 'Scheduled' },
  { value: WORKFLOW_STATUS.SEEN,              label: 'Seen' },
];

const ACTIVE_STATUSES = new Set([
  WORKFLOW_STATUS.NEW,
  WORKFLOW_STATUS.NEEDS_INFO,
  WORKFLOW_STATUS.READY_TO_SCHEDULE,
  WORKFLOW_STATUS.SCHEDULED,
  WORKFLOW_STATUS.SEEN,
]);

const TERMINAL_STATUSES = new Set([
  WORKFLOW_STATUS.CLOSED_LOOP,
  WORKFLOW_STATUS.CANCELLED,
  WORKFLOW_STATUS.NO_SHOW,
]);

const OUTCOME_TAB_FILTER_OPTIONS = [
  { value: 'ALL',                        label: 'All Outcomes' },
  { value: WORKFLOW_STATUS.CLOSED_LOOP,  label: 'Closed Loop' },
  { value: WORKFLOW_STATUS.NO_SHOW,      label: 'No-Show' },
  { value: WORKFLOW_STATUS.CANCELLED,    label: 'Cancelled' },
];

const GATE_OUTCOME_FILTER_OPTIONS = [
  { value: 'ALL',     label: 'All Outcomes' },
  { value: 'routine', label: 'Routine' },
  { value: 'urgent',  label: 'Urgent' },
  { value: 'not_yet', label: 'Not Yet' },
];

// Intake-coordinator Active Queue = Intake-owned work only. Downstream states
// (Ready to Schedule / Scheduled / Seen) move to Referral Outcomes › After Intake.
// "Ready for Intake Review" is a derived filter (NEEDS_INFO + recoveryStage), not a
// workflow.status — its value is the recoveryStage constant.
const READY_FOR_REVIEW_FILTER = RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW; // 'READY_FOR_INTAKE_REVIEW'
const INTAKE_ACTIVE_FILTER_OPTIONS = [
  { value: 'ALL',                      label: 'All' },
  { value: WORKFLOW_STATUS.NEW,        label: 'New' },
  { value: WORKFLOW_STATUS.NEEDS_INFO, label: 'Needs Info' },
  { value: READY_FOR_REVIEW_FILTER,    label: 'Ready for Intake Review' },
];

// Downstream (post-Intake) states surfaced under Referral Outcomes › After Intake.
const AFTER_INTAKE_STATUSES = new Set([
  WORKFLOW_STATUS.READY_TO_SCHEDULE,
  WORKFLOW_STATUS.SCHEDULED,
  WORKFLOW_STATUS.SEEN,
]);
const AFTER_INTAKE_FILTER_OPTIONS = [
  { value: WORKFLOW_STATUS.READY_TO_SCHEDULE, label: 'Ready to Schedule' },
  { value: WORKFLOW_STATUS.SCHEDULED,         label: 'Scheduled' },
  { value: WORKFLOW_STATUS.SEEN,              label: 'Seen' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 12 ? `…${id.slice(-8)}` : id;
}

const STUCK_DAYS = 3;

/**
 * Canonical override check.
 * Primary source: referral doc — outcome.isOverride / override.used (Gate-authored).
 * Fallback: workflow/state — overrideDecision / overriddenBy (triage-authored).
 */
function isOverridden(referral, workflow) {
  if (referral?.outcome?.isOverride === true) return true;
  if (referral?.override?.used === true)      return true;
  return !!(
    workflow?.overrideDecision ||
    workflow?.overrideReason   ||
    workflow?.overriddenBy     ||
    workflow?.overriddenAt
  );
}

function computeStuck(wf) {
  if (wf?.status !== 'NEEDS_INFO') return false;
  if (!(wf?.rework?.missingItemsCount > 0)) return false;
  const lt = wf?.rework?.lastTouchAt;
  if (!lt) return true;
  const d = lt.toDate ? lt.toDate() : new Date(lt.seconds * 1000);
  return (Date.now() - d.getTime()) > STUCK_DAYS * 86400_000;
}

// ── Sub-components ────────────────────────────────────────────────────────────


// ── Filter bar ────────────────────────────────────────────────────────────────

function FilterBar({ statusFilter, setStatusFilter, filterOptions, outcomeFilter, setOutcomeFilter, onRefresh, loading, statusCounts, activeFocusTarget }) {
  return (
   <div className="mb-6">
    <div
      data-learning-target="active_queue_tabs"
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${activeFocusTarget === 'active_queue_tabs' ? 'rounded-xl p-2 -m-2 transition-all duration-300 learning-focus-highlight-subtle' : ''}`}
    >
      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-1.5">
        {filterOptions.map(({ value, label }) => {
          const count = value !== 'ALL' ? (statusCounts?.[value] ?? 0) : null;
          return (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              {...(value === 'NEW' ? { 'data-learning-target': 'workqueue_new_tab' } : {})}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all
                ${statusFilter === value
                  ? 'bg-slate-700 text-white border border-slate-600/50'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent'}
                ${value === 'NEW' && activeFocusTarget === 'workqueue_new_tab' ? ' learning-focus-highlight-subtle' : ''}`}
            >
              {label}
              {count != null && count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold
                  ${statusFilter === value
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-700 text-slate-300'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Right side: outcome filter + refresh */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <select
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value)}
          data-learning-target="active_queue_outcome_filter"
          className="px-3 py-1.5 rounded-xl bg-slate-800 border border-slate-700/50 text-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-slate-500/30"
        >
          {GATE_OUTCOME_FILTER_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 transition-all"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
   </div>
  );
}

// ── Workqueue item card ───────────────────────────────────────────────────────

function WorkItemCard({ item, onClick, currentUserId, isFirstCard, activeFocusTarget }) {
  const { referral, workflow } = item;
  const outcomeSt  = referral.outcome?.status ?? referral.outcomeStatus ?? null;
  const isOwned     = !!workflow?.ownerUid;
  const isOwnedByMe = workflow?.ownerUid === currentUserId;
  // Row tint precedence: clinical URGENCY (Gate outcome urgent OR priority urgent)
  // dominates over operational HIGH priority, so urgent never reads as peach/orange.
  const isUrgent    = String(outcomeSt ?? '').toLowerCase() === 'urgent' || workflow?.priority === WORKFLOW_PRIORITY.URGENT;
  const isHighPri   = !isUrgent && workflow?.priority === WORKFLOW_PRIORITY.HIGH;
  const isStuck     = computeStuck(workflow);
  const overridden  = isOverridden(referral, workflow);
  const isReadyForReview = workflow?.recoveryStage === RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW;

  return (
    <button
      onClick={onClick}
      {...(isFirstCard ? { 'data-learning-target': 'workqueue_first_referral_card' } : {})}
      className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all hover:border-slate-600/50 hover:bg-slate-800/60 group
        ${isUrgent
          ? 'bg-red-500/5 border-red-500/20 hover:bg-red-500/10'
          : isHighPri
          ? 'bg-orange-500/5 border-orange-500/20 hover:bg-orange-500/10'
          : 'bg-slate-900/60 border-slate-700/50'}
        ${isFirstCard && activeFocusTarget === 'workqueue_first_referral_card' ? ' learning-focus-highlight-subtle' : ''}`}
    >
      {/* Icon */}
      <div className="w-10 h-10 rounded-xl bg-slate-800/80 border border-slate-700/40 flex items-center justify-center flex-shrink-0">
        <ClipboardList className="w-5 h-5 text-slate-400" />
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {workflow?.status && <RGStatusBadge type="workflow" status={workflow.status} />}
          {outcomeSt && <RGStatusBadge type="outcome" status={outcomeSt} />}
          {isReadyForReview && (
            <span className={`${CHIP_CLS} bg-status-new text-status-new border-status-new`}>Ready for Intake Review</span>
          )}
          {overridden && (
            <span className={`${CHIP_CLS} bg-status-warning text-status-warning border-status-warning`}>Overridden</span>
          )}
          {(isUrgent || isHighPri) && (
            <RGStatusBadge type="priority" status={workflow?.priority} />
          )}
          {isStuck && (
            <span className={`${CHIP_CLS} bg-status-urgent text-status-urgent border-status-urgent`}>Stuck</span>
          )}
        </div>
        <p className="text-sm font-medium text-white truncate">
          {referral.specialtyId ?? 'Unknown Specialty'}
          {referral.packId ? <span className="text-slate-400 font-normal"> · {referral.packId}</span> : null}
        </p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-slate-500 font-mono">{referral.id || '—'}</span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {fmtTs(referral.createdAt ?? referral.savedAt)}
          </span>
          {isOwned && (
            <span className={`text-xs flex items-center gap-1 ${isOwnedByMe ? 'text-teal-400' : 'text-slate-400'}`}>
              <User className="w-3 h-3" />
              {isOwnedByMe ? 'You' : 'Assigned'}
            </span>
          )}
        </div>
      </div>

      {/* Chevron */}
      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
    </button>
  );
}

// ── Outcome sort (terminal states) ────────────────────────────────────────────

function tsSec(ts) {
  if (!ts) return null;
  if (typeof ts.seconds === 'number') return ts.seconds;
  if (typeof ts.toMillis === 'function') return ts.toMillis() / 1000;
  return null;
}

function sortOutcomes(arr) {
  return [...arr].sort((a, b) => {
    const ca = tsSec(a.workflow?.closedLoop?.closedAt);
    const cb = tsSec(b.workflow?.closedLoop?.closedAt);
    if (ca !== cb) { if (ca === null) return 1; if (cb === null) return -1; return cb - ca; }
    const sa = tsSec(a.workflow?.scheduling?.seenAt);
    const sb = tsSec(b.workflow?.scheduling?.seenAt);
    if (sa !== sb) { if (sa === null) return 1; if (sb === null) return -1; return sb - sa; }
    return (tsSec(b.workflow?.updatedAt) ?? 0) - (tsSec(a.workflow?.updatedAt) ?? 0);
  });
}

// ── Outcome Card (terminal states: Closed Loop, Cancelled, No-Show) ──────────

const OUTCOME_ICON_CONFIG = {
  [WORKFLOW_STATUS.CLOSED_LOOP]:       { Icon: CheckCircle2, iconTw: 'text-emerald-400', bgTw: 'bg-emerald-500/10 border-emerald-500/20' },
  [WORKFLOW_STATUS.CANCELLED]:         { Icon: AlertCircle,  iconTw: 'text-red-400',     bgTw: 'bg-red-500/10 border-red-500/20' },
  [WORKFLOW_STATUS.NO_SHOW]:           { Icon: Clock,        iconTw: 'text-orange-400',   bgTw: 'bg-orange-500/10 border-orange-500/20' },
  // After-Intake (post-handoff) states surfaced under Referral Outcomes for intake.
  [WORKFLOW_STATUS.READY_TO_SCHEDULE]: { Icon: ClipboardList, iconTw: 'text-violet-400', bgTw: 'bg-violet-500/10 border-violet-500/20' },
  [WORKFLOW_STATUS.SCHEDULED]:         { Icon: Clock,        iconTw: 'text-teal-400',     bgTw: 'bg-teal-500/10 border-teal-500/20' },
  [WORKFLOW_STATUS.SEEN]:              { Icon: CheckCircle2, iconTw: 'text-teal-400',     bgTw: 'bg-teal-500/10 border-teal-500/20' },
};

function OutcomeCard({ item, onClick }) {
  const { referral, workflow } = item;
  const status      = workflow?.status;
  const outcomeSt   = referral.outcome?.status ?? referral.outcomeStatus ?? null;
  const priority    = workflow?.priority;
  const showPriority = priority && priority !== WORKFLOW_PRIORITY.NORMAL;
  const iconCfg     = OUTCOME_ICON_CONFIG[status] ?? OUTCOME_ICON_CONFIG[WORKFLOW_STATUS.CLOSED_LOOP];
  const { Icon, iconTw, bgTw } = iconCfg;

  const closedAt    = workflow?.closedLoop?.closedAt;
  const cancelledAt = workflow?.cancelled?.cancelledAt;
  const resolvedAt  = closedAt ?? cancelledAt ?? workflow?.updatedAt;

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all hover:border-slate-600/50 hover:bg-slate-800/60 group bg-slate-900/60 border-slate-700/50"
    >
      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${bgTw}`}>
        <Icon className={`w-5 h-5 ${iconTw}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {status && <RGStatusBadge type="workflow" status={status} />}
          {outcomeSt && <RGStatusBadge type="outcome" status={outcomeSt} />}
          {showPriority && <RGStatusBadge type="priority" status={priority} />}
        </div>
        <p className="text-sm font-medium text-white truncate">
          {referral.specialtyId ?? 'Unknown Specialty'}
          {referral.packId && <span className="text-slate-400 font-normal"> · {referral.packId}</span>}
        </p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-slate-500 font-mono">{referral.id || '—'}</span>
          {resolvedAt && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Icon className="w-3 h-3" />
              {fmtTs(resolvedAt)}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
    </button>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function WorkqueueListView({ activeFocusTarget, onModalContext, autoOpenReferralId }) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const PAGE_SIZE = 25;
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState('');
  const [statusFilter, setStatusFilter]   = useState(
    isIntakeCoordinator(user?.role) ? WORKFLOW_STATUS.NEW : 'ALL'
  );
  const [outcomeFilter, setOutcomeFilter] = useState('ALL');
  const [selectedItem, setSelectedItem]   = useState(null);
  const [visibleCount, setVisibleCount]   = useState(PAGE_SIZE);
  const [deepLinkNotice, setDeepLinkNotice] = useState(''); // shown if deep-linked referral isn't here
  const autoOpenFired = useRef(false);

  // ── Referral Outcomes tab state ─────────────────────────────────────────────
  const [activeTab,           setActiveTab]           = useState('active'); // 'active' | 'outcomes'
  const [outcomeItems,        setOutcomeItems]        = useState([]);
  const [outcomeLoaded,       setOutcomeLoaded]       = useState(false);
  const [outcomeLoading,      setOutcomeLoading]      = useState(false);
  const [outcomeError,        setOutcomeError]        = useState(null);
  const [outcomeVisibleCount, setOutcomeVisibleCount] = useState(PAGE_SIZE);
  const [outcomeStatusFilter, setOutcomeStatusFilter] = useState('ALL');

  // Auto-switch tabs when learning focus targets require a specific tab to be visible.
  useEffect(() => {
    if (activeFocusTarget === 'referral_outcomes_tab') {
      setActiveTab('outcomes');
    } else if (activeFocusTarget === 'active_queue_tabs' || activeFocusTarget === 'workqueue_new_tab' || activeFocusTarget === 'workqueue_first_referral_card' || activeFocusTarget === 'workqueue_main_list') {
      setActiveTab('active');
    }
  }, [activeFocusTarget]);

  // Scroll focus target into view when it changes.
  useEffect(() => {
    if (!activeFocusTarget) return;
    const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeFocusTarget]);

  // Reset page when filters change so new results always start from the top.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [statusFilter, outcomeFilter]);

  // Reset incompatible filters on tab switch.
  const handleTabSwitch = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'active') {
      setStatusFilter(isIntakeCoordinator(user?.role) ? WORKFLOW_STATUS.NEW : 'ALL');
      setOutcomeFilter('ALL');
    } else {
      setOutcomeStatusFilter('ALL');
    }
  }, [user?.role]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError('');
    try {
      // Active queue must show every active referral (matches Live Fires), not just
      // the 50 most-recent referrals — otherwise older NEW items are hidden from intake
      // and the New-tab count undercounts vs the Unclaimed New fire card.
      const data = await listOpsActiveItems(tenantId);
      setItems(data);
    } catch (err) {
      setLoadError(safeUserError(err) ?? 'Failed to load workqueue. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: when arriving from "Open Referral" (Referral Pings), auto-open the
  // exact WorkItemDrawer for autoOpenReferralId — once, after items finish loading.
  // Activates the matching status tab so the row is visible. Shows a clear notice
  // if the referral isn't in the active workspace.
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading) return;
    autoOpenFired.current = true;
    const res = findReferralInBuckets([{ tab: 'active', items }], autoOpenReferralId);
    if (res.found) {
      setActiveTab('active');
      const st = res.item.workflow?.status;
      if (st && ACTIVE_STATUSES.has(st)) setStatusFilter(st);
      setSelectedItem(res.item);
      onModalContext?.('referral-detail');
    } else {
      // Not in the active queue — read-only fallback (non-PHI core doc) for a
      // specific, truthful reason instead of a vague "not available".
      getReferral(tenantId, autoOpenReferralId)
        .then((ref) => setDeepLinkNotice(classifyDeepLinkFailure({ referral: ref })))
        .catch((err) => setDeepLinkNotice(classifyDeepLinkFailure({ error: err })));
    }
  }, [autoOpenReferralId, loading, items, onModalContext, tenantId]);

  const loadOutcomes = useCallback(async () => {
    if (!tenantId) return;
    setOutcomeLoading(true);
    setOutcomeError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 200 });
      setOutcomeItems(sortOutcomes(data.filter(({ workflow }) => TERMINAL_STATUSES.has(workflow?.status))));
      setOutcomeLoaded(true);
    } catch (err) {
      setOutcomeError(safeUserError(err) ?? 'Failed to load referral outcomes. Please retry.');
    } finally {
      setOutcomeLoading(false);
    }
  }, [tenantId]);

  // Lazy load outcomes tab on first open; reset page on tab switch.
  useEffect(() => { if (activeTab === 'outcomes' && !outcomeLoaded) loadOutcomes(); }, [activeTab, outcomeLoaded, loadOutcomes]);
  useEffect(() => { setOutcomeVisibleCount(PAGE_SIZE); }, [activeTab]);

  // After a workflow write, patch the item in-place so the list reflects the change.
  const handleWorkflowUpdated = useCallback((referralId, newWorkflow) => {
    setItems((prev) =>
      prev.map((it) =>
        it.referral.id === referralId ? { ...it, workflow: newWorkflow } : it
      )
    );
    // Also patch the selected item if it's the one being updated
    setSelectedItem((prev) =>
      prev && prev.referral.id === referralId
        ? { ...prev, workflow: newWorkflow }
        : prev
    );
  }, []);

  const isIntake = isIntakeCoordinator(user?.role);

  // Role-aware Active Queue filter set. Intake sees only Intake-owned work
  // (New / Needs Info / Ready for Intake Review); other roles see the full pipeline.
  const activeFilterOptions = isIntake ? INTAKE_ACTIVE_FILTER_OPTIONS : ACTIVE_FILTER_OPTIONS;

  // A NEEDS_INFO referral that recovered and is awaiting Intake clearance.
  const isReadyForReview = (wf) =>
    wf?.status === WORKFLOW_STATUS.NEEDS_INFO
    && wf?.recoveryStage === RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW;

  // For intake, the Active Queue is only NEW + NEEDS_INFO (downstream states move to
  // Referral Outcomes › After Intake). Other roles keep all active statuses.
  const activeItems = items.filter(({ workflow }) => {
    if (!ACTIVE_STATUSES.has(workflow?.status)) return false;
    if (isIntake && AFTER_INTAKE_STATUSES.has(workflow?.status)) return false;
    return true;
  });

  // Count per filter (unaffected by current selection). Intake splits NEEDS_INFO into
  // Needs Info (still awaiting) vs Ready for Intake Review (recovered).
  const statusCounts = activeItems.reduce((acc, it) => {
    const s = it.workflow?.status;
    if (!s) return acc;
    if (isIntake && s === WORKFLOW_STATUS.NEEDS_INFO) {
      const key = isReadyForReview(it.workflow) ? READY_FOR_REVIEW_FILTER : WORKFLOW_STATUS.NEEDS_INFO;
      acc[key] = (acc[key] ?? 0) + 1;
    } else {
      acc[s] = (acc[s] ?? 0) + 1;
    }
    return acc;
  }, {});

  // Client-side filtering (active queue only).
  const filtered = activeItems.filter((it) => {
    const { referral, workflow } = it;
    const status = workflow?.status ?? null;
    if (isIntake) {
      // Intake filters: ALL = NEW+NEEDS_INFO; NEEDS_INFO excludes recovered; review = recovered.
      if (statusFilter === WORKFLOW_STATUS.NEW) {
        if (status !== WORKFLOW_STATUS.NEW) return false;
      } else if (statusFilter === WORKFLOW_STATUS.NEEDS_INFO) {
        if (!(status === WORKFLOW_STATUS.NEEDS_INFO && !isReadyForReview(workflow))) return false;
      } else if (statusFilter === READY_FOR_REVIEW_FILTER) {
        if (!isReadyForReview(workflow)) return false;
      }
      // 'ALL' → keep all (activeItems already excludes downstream for intake)
    } else {
      if (statusFilter !== 'ALL' && status !== statusFilter) return false;
    }
    const outcomeSt = referral.outcome?.status ?? referral.outcomeStatus ?? null;
    if (outcomeFilter !== 'ALL' && outcomeSt !== outcomeFilter) return false;
    return true;
  });

  // Referral Outcomes source: terminal outcomes + (for intake) post-Intake states.
  const afterIntakeItems = isIntake
    ? sortOutcomes(items.filter(({ workflow }) => AFTER_INTAKE_STATUSES.has(workflow?.status)))
    : [];
  const outcomeSourceItems = isIntake ? [...afterIntakeItems, ...outcomeItems] : outcomeItems;
  const outcomeFilterOptions = isIntake
    ? [...OUTCOME_TAB_FILTER_OPTIONS.slice(0, 1), ...AFTER_INTAKE_FILTER_OPTIONS, ...OUTCOME_TAB_FILTER_OPTIONS.slice(1)]
    : OUTCOME_TAB_FILTER_OPTIONS;

  // Outcome tab filtering by status.
  const filteredOutcomes = outcomeStatusFilter === 'ALL'
    ? outcomeSourceItems
    : outcomeSourceItems.filter(({ workflow }) => workflow?.status === outcomeStatusFilter);

  // Tiered sort (intake coordinator canonical order):
  //   Tier 0: URGENT + NEW             — critical, needs immediate intake action
  //   Tier 1: URGENT + NEEDS_INFO      — urgent, GP must send docs
  //   Tier 2: ROUTINE + NEW            — needs intake review
  //   Tier 3: ROUTINE + NEEDS_INFO     — waiting on GP docs
  //   Tier 4: any other NEEDS_INFO     — e.g. not_yet outcome + NEEDS_INFO
  //   Tier 5: URGENT + READY_TO_SCHEDULE
  //   Tier 6: ROUTINE + READY_TO_SCHEDULE
  //   Tier 7: everything else (SCHEDULED, SEEN, CLOSED_LOOP…)
  //
  // Invariant: ALL NEEDS_INFO tiers (1–4) appear before ALL READY_TO_SCHEDULE
  // tiers (5–6) because NEEDS_INFO still requires GP action.
  // Secondary: priority DESC → statusEnteredAt ASC → createdAt ASC
  function getTier(referral, workflow) {
    const status  = workflow?.status ?? '';
    const outcome = (
      referral?.finalOutcome ??
      referral?.outcome?.status ??
      ''
    ).toLowerCase();
    // Not-Yet recovery: a recovered referral (recoveryStage READY_FOR_INTAKE_REVIEW)
    // is intake-actionable NOW. Surface it at the top of the Needs Info lane, urgent
    // ahead of routine, before any still-awaiting-GP or intake-created Needs Info rows.
    const recovered = workflow?.recoveryStage === RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW;
    const recoveredOutcome = (workflow?.recoveredOutcome ?? outcome).toLowerCase();
    if (recovered && status === WORKFLOW_STATUS.NEEDS_INFO) {
      return recoveredOutcome === 'urgent' ? 1 : 3;
    }
    if (outcome === 'urgent'  && status === WORKFLOW_STATUS.NEW)               return 0;
    if (outcome === 'urgent'  && status === WORKFLOW_STATUS.NEEDS_INFO)        return 1;
    if (outcome === 'routine' && status === WORKFLOW_STATUS.NEW)               return 2;
    if (outcome === 'routine' && status === WORKFLOW_STATUS.NEEDS_INFO)        return 3;
    if (status === WORKFLOW_STATUS.NEEDS_INFO)                                 return 4;
    if (outcome === 'urgent'  && status === WORKFLOW_STATUS.READY_TO_SCHEDULE) return 5;
    if (outcome === 'routine' && status === WORKFLOW_STATUS.READY_TO_SCHEDULE) return 6;
    return 7;
  }

  const PRIORITY_RANK = { URGENT: 0, HIGH: 1, NORMAL: 2, urgent: 0, high: 1, normal: 2 };

  function tsSeconds(ts) {
    if (!ts) return 0;
    return ts.seconds ?? (ts.toMillis ? ts.toMillis() / 1000 : 0);
  }

  const sorted = [...filtered].sort((a, b) => {
    // 1. Tier
    const ta = getTier(a.referral, a.workflow);
    const tb = getTier(b.referral, b.workflow);
    if (ta !== tb) return ta - tb;

    // 2. Priority DESC (lower rank = higher priority)
    const pa = PRIORITY_RANK[a.workflow?.priority] ?? 2;
    const pb = PRIORITY_RANK[b.workflow?.priority] ?? 2;
    if (pa !== pb) return pa - pb;

    // 3. statusEnteredAt ASC (oldest in current status first)
    const sa = tsSeconds(a.workflow?.statusEnteredAt ?? a.workflow?.updatedAt);
    const sb = tsSeconds(b.workflow?.statusEnteredAt ?? b.workflow?.updatedAt);
    if (sa !== sb) return sa - sb;

    // 4. createdAt ASC (oldest referral first)
    const ca = tsSeconds(a.referral?.createdAt ?? a.referral?.savedAt);
    const cb = tsSeconds(b.referral?.createdAt ?? b.referral?.savedAt);
    return ca - cb;
  });

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-slate-700/50 flex items-center justify-center">
            <List className="w-5 h-5 text-slate-300" />
          </div>
          <h1 className="text-2xl font-semibold text-white">Workqueue</h1>
        </div>
        <p className="text-slate-400 text-sm ml-[52px]">
          Manage and progress referrals through the intake pipeline.
        </p>
      </div>

      {/* ── Main Tab Bar ── */}
      <div className="flex gap-1 border-b border-slate-800/60 mb-6">
        {[
          { id: 'active',   label: 'Active Queue',       color: 'text-slate-300 border-slate-400',     learningTarget: null },
          { id: 'outcomes', label: 'Referral Outcomes',   color: 'text-emerald-400 border-emerald-500', learningTarget: 'referral_outcomes_tab' },
        ].map(({ id, label, color, learningTarget }) => (
          <button
            key={id}
            type="button"
            onClick={() => handleTabSwitch(id)}
            {...(learningTarget ? { 'data-learning-target': learningTarget } : {})}
            className={[
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px',
              activeTab === id
                ? `${color} bg-transparent`
                : 'text-slate-500 border-transparent hover:text-slate-300',
              learningTarget && activeFocusTarget === learningTarget
                ? 'rounded-t-lg learning-focus-highlight-subtle'
                : '',
            ].join(' ')}
          >
            {label}
            {id === 'outcomes' && outcomeLoaded && (
              <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold bg-slate-800/60 text-slate-600">
                {outcomeItems.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Active Queue ── */}
      {activeTab === 'active' && (
        <>
          {/* Filter bar */}
          <FilterBar
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            filterOptions={activeFilterOptions}
            outcomeFilter={outcomeFilter}
            setOutcomeFilter={setOutcomeFilter}
            onRefresh={load}
            loading={loading}
            statusCounts={statusCounts}
            activeFocusTarget={activeFocusTarget}
          />

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      )}

      {/* Load error */}
      {deepLinkNotice && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      {!loading && loadError && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-400">{loadError}</p>
            <button
              onClick={load}
              className="mt-2 text-xs text-red-400 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Empty state — no active items at all */}
      {!loading && !loadError && activeItems.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No active referrals</h3>
          <p className="text-slate-400 text-sm">
            Referrals in the intake pipeline will appear here for processing.
          </p>
        </div>
      )}

      {/* Empty state — filter returned nothing */}
      {!loading && !loadError && activeItems.length > 0 && sorted.length === 0 && (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-3">
            <Filter className="w-7 h-7 text-slate-500" />
          </div>
          <h3 className="text-base font-medium text-white mb-1">No matches</h3>
          <p className="text-slate-400 text-sm">
            No active referrals match the current filters. Try adjusting the status or outcome filter.
          </p>
        </div>
      )}

      {/* Item list */}
      {!loading && !loadError && sorted.length > 0 && (
        <>
          <div className={`space-y-2 ${activeFocusTarget === 'workqueue_main_list' ? 'rounded-xl transition-all duration-300 learning-focus-highlight-subtle' : ''}`} data-learning-target="workqueue_main_list">
            {sorted.slice(0, visibleCount).map((item, idx) => (
              <WorkItemCard
                key={item.referral.id}
                item={item}
                onClick={() => { setSelectedItem(item); onModalContext?.('referral-detail'); }}
                currentUserId={user?.uid}
                isFirstCard={idx === 0}
                activeFocusTarget={activeFocusTarget}
              />
            ))}
          </div>
          {sorted.length > visibleCount ? (
            <div className="pt-3 pb-1 flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all"
              >
                Load more
              </button>
              <p className="text-[11px] text-slate-600">{sorted.length - visibleCount} remaining</p>
            </div>
          ) : (
            <p className="text-xs text-slate-600 text-center mt-6">
              Showing {Math.min(visibleCount, sorted.length)} of {sorted.length} referral{sorted.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}
        </>
      )}

      {/* ── Referral Outcomes Tab ── */}
      {activeTab === 'outcomes' && (
        <>
          {/* After Intake group caption (intake only) */}
          {isIntake && outcomeSourceItems.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
              After Intake — Ready to Schedule, Scheduled, Seen (post-handoff visibility)
            </p>
          )}
          {/* Outcome sub-filters */}
          {outcomeSourceItems.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-6">
              {outcomeFilterOptions.map(({ value, label }) => {
                const count = value !== 'ALL' ? outcomeSourceItems.filter(({ workflow }) => workflow?.status === value).length : null;
                return (
                  <button
                    key={value}
                    onClick={() => { setOutcomeStatusFilter(value); setOutcomeVisibleCount(PAGE_SIZE); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all
                      ${outcomeStatusFilter === value
                        ? 'bg-slate-700 text-white border border-slate-600/50'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent'}`}
                  >
                    {label}
                    {count != null && count > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold
                        ${outcomeStatusFilter === value
                          ? 'bg-white/20 text-white'
                          : 'bg-slate-700 text-slate-300'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {outcomeLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          )}
          {!outcomeLoading && outcomeError && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-400">{outcomeError}</p>
                <button onClick={loadOutcomes} className="mt-2 text-xs text-red-400 underline hover:no-underline">
                  Try again
                </button>
              </div>
            </div>
          )}
          {!outcomeLoading && !outcomeError && outcomeSourceItems.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-slate-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No referral outcomes yet</h3>
              <p className="text-slate-400 text-sm">
                {isIntake
                  ? 'Referrals handed forward (Ready to Schedule, Scheduled, Seen) or reaching a terminal state will appear here.'
                  : 'Referrals that reach a terminal state (Closed Loop, Cancelled, or No-Show) will appear here.'}
              </p>
            </div>
          )}
          {!outcomeLoading && !outcomeError && outcomeSourceItems.length > 0 && filteredOutcomes.length === 0 && (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-3">
                <Filter className="w-7 h-7 text-slate-500" />
              </div>
              <h3 className="text-base font-medium text-white mb-1">No matches</h3>
              <p className="text-slate-400 text-sm">
                No outcomes match the selected filter. Try a different outcome type.
              </p>
            </div>
          )}
          {!outcomeLoading && !outcomeError && filteredOutcomes.length > 0 && (
            <>
              <div className="space-y-2">
                {filteredOutcomes.slice(0, outcomeVisibleCount).map((item) => (
                  <OutcomeCard
                    key={item.referral.id}
                    item={item}
                    onClick={() => { setSelectedItem(item); onModalContext?.('referral-detail'); }}
                  />
                ))}
              </div>
              {filteredOutcomes.length > outcomeVisibleCount ? (
                <div className="pt-3 pb-1 flex flex-col items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setOutcomeVisibleCount((c) => c + PAGE_SIZE)}
                    className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all"
                  >
                    Load more
                  </button>
                  <p className="text-[11px] text-slate-600">{filteredOutcomes.length - outcomeVisibleCount} remaining</p>
                </div>
              ) : (
                <p className="text-xs text-slate-600 text-center mt-6">
                  Showing {Math.min(outcomeVisibleCount, filteredOutcomes.length)} of {filteredOutcomes.length} outcome{filteredOutcomes.length !== 1 ? 's' : ''}
                </p>
              )}
            </>
          )}
        </>
      )}

      {/* Drawer */}
      {selectedItem && (
        <WorkItemDrawer
          item={selectedItem}
          onClose={() => { setSelectedItem(null); onModalContext?.(null); }}
          onWorkflowUpdated={handleWorkflowUpdated}
          activeFocusTarget={activeFocusTarget}
        />
      )}
    </div>
  );
}
